import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useCookies} from "@repo/data-cookies";
import {doNotTranslate} from "@repo/i18n";
import {RequestInfo} from "@repo/shared-core";
import {redirect, reload} from "@repo/urls";

import {getAuthCookieNames} from "../data/get-auth-cookie-names.ts";

type Props = {
    title: string;
    message: string;
};

const ErrorModal = (props: Props): React.ReactElement => {
    const [updating, setUpdating] = React.useState(false);
    const refreshButton = React.useRef(null);
    const cookies = useCookies();

    React.useEffect(() => {
        // We focus the refreshButton manually here instead of
        // using ModalLauncher's `initialFocusId` prop.  This
        // makes this component more self-contained avoid the
        // need for the parent to coordinate identifiers between
        // the launcher and this component.
        // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'never'.
        refreshButton.current?.focus();
    }, []); // NOTE: This effect runs only once because its deps array is empty

    const handleLogout = () => {
        redirect("/logout");
        setUpdating(true);
    };

    const handleRefresh = () => {
        reload();
        setUpdating(true);
    };

    const handleClearStorage = () => {
        for (const name of getAuthCookieNames()) {
            cookies?.eraseCookie(name);
        }
        localStorage.removeItem("use_dev_backend");
        localStorage.removeItem("use_prod_backend");

        // We redirect to the homepage since the current URL may be inaccessible
        // to a logged out user.
        redirect("/");
        setUpdating(true);
    };

    const {title, message} = props;

    const content = (
        <View style={styles.content}>
            <Body>{message}</Body>
            <Spring />
            <Strut size={spacing.medium_16} />
            <View style={styles.buttonContainer}>
                {RequestInfo.IS_DEV_SERVER && (
                    <Button
                        onClick={handleClearStorage}
                        kind="secondary"
                        disabled={updating || !cookies}
                    >
                        {doNotTranslate`Clear cookies and refresh`}
                    </Button>
                )}
                <Strut size={spacing.medium_16} />
                <Button
                    onClick={handleLogout}
                    kind="secondary"
                    disabled={updating}
                >
                    {t`Log out`}
                </Button>
                <Strut size={spacing.medium_16} />
                <Button
                    onClick={handleRefresh}
                    disabled={updating}
                    ref={refreshButton}
                >
                    {t`Refresh`}
                </Button>
            </View>
        </View>
    );

    return (
        <OnePaneDialog
            closeButtonVisible={false}
            title={title}
            content={content}
            style={styles.dialog}
            role="alertdialog"
        />
    );
};

const styles = StyleSheet.create({
    buttonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    content: {
        flexGrow: 1,
    },
    dialog: {
        height: 300,
    },
});

export default ErrorModal;
